import { formatDateTime } from '@/utils/formatDate'

export type FinanceLockReason =
  | 'decider_hr'
  | 'not_in_play'
  | 'settled'
  | 'hr_override'
  | 'binding'
  | 'payer_not_private'
  | null

interface Option {
  value: string
  label: string
}

interface FinanceChoiceRowProps {
  groupName: string
  title: string
  options: Option[]
  personalValue: string | null
  schoolDefault: string
  canEdit: boolean
  lockReason?: FinanceLockReason
  bindingAt?: string | null
  pendingHint?: string | null
  saving?: boolean
  error?: string | null
  onSwitch: (value: string) => void
}

const LOCK_TEXT: Record<string, string> = {
  binding: 'bindend',
  hr_override: 'von HR festgelegt',
  settled: 'abgerechnet',
  payer_not_private: 'kein privater Zahler',
}

export function FinanceChoiceRow({
  groupName,
  title,
  options,
  personalValue,
  schoolDefault,
  canEdit,
  lockReason,
  bindingAt,
  pendingHint,
  saving,
  error,
  onSwitch,
}: FinanceChoiceRowProps) {
  const display = personalValue ?? schoolDefault
  const showStandard = personalValue == null && canEdit

  if (pendingHint) {
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded px-2.5 py-1.5">
        {pendingHint}
      </div>
    )
  }

  const lockText = lockReason ? LOCK_TEXT[lockReason] : null

  return (
    <div className="flex items-center gap-3 flex-wrap text-xs bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded px-2.5 py-1.5">
      <span className="font-medium text-indigo-800 dark:text-indigo-200">{title}</span>
      {options.map(({ value, label }) => (
        <label
          key={value}
          className={`inline-flex items-center gap-1.5 text-gray-700 dark:text-gray-200 ${
            canEdit && !saving ? 'cursor-pointer' : 'cursor-default opacity-80'
          }`}
        >
          <input
            type="radio"
            name={groupName}
            value={value}
            checked={display === value}
            disabled={!canEdit || !!saving}
            onChange={() => {
              if (!canEdit || saving) return
              if (value === personalValue) return
              if (personalValue == null && value === schoolDefault) return
              onSwitch(value)
            }}
          />
          {label}
        </label>
      ))}
      {showStandard && <span className="text-gray-400">Schul-Standard</span>}
      {canEdit && bindingAt && (
        <span className="text-gray-400">änderbar bis {formatDateTime(bindingAt)}</span>
      )}
      {!canEdit && lockText && <span className="text-gray-500">{lockText}</span>}
      {saving && <span className="text-indigo-500">Speichern…</span>}
      {error && <span className="text-red-600">{error}</span>}
    </div>
  )
}
